const chartStates = new WeakMap()

export const chartDatasetSyncPlugin = {
  id: 'chartDatasetSync',

  afterUpdate(chart, args, options) {
    const currentIds = chart.data.datasets.map(
      (dataset, index) => dataset.legendId ?? `${dataset.legendType ?? 'dataset'}-${dataset.label ?? ''}-${index}`,
    )

    const previousIds = chartStates.get(chart)

    chartStates.set(chart, currentIds)

    if (!previousIds) return

    const changed =
      previousIds.length !== currentIds.length || previousIds.some((id, index) => id !== currentIds[index])

    if (changed) {
      options?.onDatasetsChanged?.(chart)
    }
  },

  afterDestroy(chart) {
    chartStates.delete(chart)
  },
}

export const dataLabelPlugin = {
  id: 'dataLabelPlugin',

  afterDraw: (chart, args, options) => {
    if (!options.visible) return

    const { ctx, chartArea } = chart
    const { left, right, top, bottom } = chartArea

    ctx.save()
    ctx.font = '12px Arial'
    ctx.textBaseline = 'middle'
    ctx.textAlign = 'left'

    const clamp = (v, min, max) => Math.max(min, Math.min(max, v))

    for (let x = 0; x < chart.data.datasets.length; x++) {
      const dataset = chart.data.datasets[x]

      if (dataset.legendType === 'errorEllipse') continue
      if (!chart.isDatasetVisible(x)) continue

      const meta = chart.getDatasetMeta(x)

      for (let i = 0; i < meta.data.length; i++) {
        const dataPoint = dataset.data[i]
        if (!dataPoint?.label) continue

        // The actual pixel position of the data point.
        const anchorX = meta.data[i].x
        const anchorY = meta.data[i].y

        // If the point itself is outside the visible chart area,
        // don't draw its label.
        if (anchorX < left || anchorX > right || anchorY < top || anchorY > bottom) {
          continue
        }

        ctx.fillStyle = dataPoint.color ?? options.color

        const lines = String(dataPoint.label).split(/\r?\n/)
        const lineHeight = 14
        const padding = 4

        const textWidth = Math.max(...lines.map((line) => ctx.measureText(line).width))

        const textHeight = lines.length * lineHeight

        let xCoord = anchorX
        let yCoord = anchorY - 10

        switch (options.textAlign) {
          case 'center':
            xCoord -= textWidth / 2
            break

          case 'right':
            xCoord -= textWidth
            break

          case 'left':
          default:
            break
        }

        // Keep the entire multi-line label inside the chart
        xCoord = clamp(xCoord, left + padding, right - textWidth - padding)

        yCoord = clamp(yCoord, top + padding + lineHeight / 2, bottom - textHeight + lineHeight / 2 - padding)

        // Draw each line
        lines.forEach((line, index) => {
          ctx.fillText(line, xCoord, yCoord + index * lineHeight)
        })
      }
    }

    ctx.restore()
  },
}

export const fillBetweenLinesPlugin = {
  id: 'fillBetweenLinesPlugin',
  afterDatasetDraw(chart, args) {
    const datasets = chart?.config?.data?.datasets ?? []
    if (!datasets.length) return

    const index1 = args.index
    const index2 = datasets[index1]?.fillBetweenSeries

    if (index2 == null) return
    if (index2 < 0) return
    if (index2 >= datasets.length) return

    const meta1 = chart.getDatasetMeta(index1)
    const meta2 = chart.getDatasetMeta(index2)
    const data1 = meta1?.data
    const data2 = meta2?.data

    const { ctx, chartArea } = chart
    if (!ctx || !chartArea) return

    ctx.save()
    try {
      ctx.beginPath()
      ctx.rect(chartArea.left, chartArea.top, chartArea.right - chartArea.left, chartArea.bottom - chartArea.top)
      ctx.clip()

      if (!Array.isArray(data1) || !Array.isArray(data2)) return
      if (data1.length !== data2.length) return
      if (data1.length < 2) return

      ctx.fillStyle = datasets[index1]?.fillBetweenColor || 'rgba(255, 0, 0, 0.2)'

      ctx.beginPath()
      ctx.moveTo(data1[0].x, data1[0].y)
      for (let i = 1; i < data1.length; i++) {
        ctx.lineTo(data1[i].x, data1[i].y)
      }
      for (let i = data2.length - 1; i >= 0; i--) {
        ctx.lineTo(data2[i].x, data2[i].y)
      }
      ctx.closePath()
      ctx.fill()
    } finally {
      ctx.restore()
    }
  },
}

export const shadingAreaPluginX = {
  id: `shadingAreaPluginX`,
  beforeDatasetDraw: (chart, args, options) => {
    if (!options.showShading && !args.meta._dataset.showShading) return
    const { ctx } = chart
    if (chart.config.data.datasets.length === 0) return

    let scale = chart.scales.x.width

    let numPoints = args.meta.data.length
    if (numPoints <= 1) return

    ctx.save()
    ctx.globalAlpha = 0.3
    ctx.fillStyle = args?.meta?._dataset?.color || options.color || 'rgba(255, 0, 0, 0.2)'
    ctx.rect(
      chart.chartArea.left,
      chart.chartArea.top,
      chart.chartArea.right - chart.chartArea.left,
      chart.chartArea.bottom - chart.chartArea.top,
    )
    ctx.clip()

    ctx.beginPath()
    for (let i = 0; i < numPoints; i++) {
      let dataPoint = args.meta.data[i]

      let xCoord = args.meta.data[i].x
      let yCoord = args.meta.data[i].y

      if (i === 0) ctx.moveTo(xCoord, yCoord)

      let tickHeight = (dataPoint.maxX ? dataPoint.maxX : 1) * scale
      ctx.lineTo(xCoord - tickHeight, yCoord)
    }

    for (let i = numPoints - 1; i >= 0; i--) {
      let dataPoint = args.meta.data[i]

      let xCoord = args.meta.data[i].x
      let yCoord = args.meta.data[i].y

      if (i === numPoints - 1) ctx.lineTo(xCoord, yCoord)

      let tickHeight = (dataPoint.minX ? dataPoint.minX : 0) * scale
      ctx.lineTo(xCoord - tickHeight, yCoord)

      if (i === 0) ctx.lineTo(xCoord, yCoord)
    }

    ctx.closePath()
    ctx.fill()

    ctx.restore()
  },
}

export const customAnnotation = {
  id: 'customAnnotation',
  afterDatasetsDraw: (chart) => {
    const { ctx, chartArea, _metasets } = chart
    const { left, right, top, bottom } = chartArea

    const hideCustomLabels = chart.config?._config?.options?.plugins?.hideCustomLabels
    if (hideCustomLabels) return

    ctx.save()
    ctx.font = 'bolder 10px Arial'
    ctx.textBaseline = 'middle'
    ctx.textAlign = 'left'

    const padding = 4

    _metasets.forEach((meta) => {
  if (!chart.isDatasetVisible(meta.index)) return
      if (!meta?._dataset?.label?.includes('@')) return
      if (!meta.data?.length) return

      const label = meta._dataset.label
        .replace('(gpm)', '')
        .replace('(lpm)', '')
        .replace('Usft/min', '')
        .replace('Ft/min', '')
        .replace('m/min', '')
        .replace('(Usft/hr)', '')
        .replace('(Ft/hr)', '')
        .replace('(m/hr)', '')
        .replace('(Usft)', '')
        .replace('(ft)', '')
        .replace('(m)', '')
        .replace('(Ft)', '')
        .replace('(klbs)', '')
        .replace('(tons)', '')
        .replace('klbs', '')
        .replace('tons', '')
        .replace('/100.0', '')
        .replace('/30.0', '')
        .replace('/10.0', '')
        .split('@')[1]

      if (!label) return

      const point = meta.data[meta.data.length - 1]
      const textWidth = ctx.measureText(label).width

      const x = point.x + 12
      const y = point.y - 10

      if (x < left + padding || x + textWidth > right - padding || y < top + padding || y > bottom - padding) {
        return
      }

      ctx.fillStyle = meta._dataset.borderColor
      ctx.fillText(label, x, y)
    })

    ctx.restore()
  },
}

export const GradientPlugIn = {
  id: 'GradientPlugIn',
  beforeDraw: (chart, args, options) => {
    if (options?.showGradient === false) return
    const ctx = chart.ctx
    const canvas = chart.canvas
    const chartArea = chart.chartArea
    if (chartArea) {
      const chartWidth = chartArea.right - chartArea.left
      const chartHeight = chartArea.bottom - chartArea.top

      var gradient = canvas
        .getContext('2d')
        .createLinearGradient(chartWidth / 2, chartArea.top, chartWidth / 2, chartArea.bottom)
      gradient.addColorStop(0, options?.theme === 'light' ? '#ffffff' : '#141414')
      gradient.addColorStop(1, options?.theme === 'light' ? '#c0c0c0' : '#282828')

      ctx.fillStyle = gradient
      ctx.fillRect(chartArea.left, chartArea.top, chartWidth, chartHeight)
    }
  },
}

export const ChartBackgroundPlugIn = {
  id: 'ChartBackgroundPlugIn',
  beforeDraw: (chart, args, options) => {
    if (options?.showChartBackground !== true) return
    const ctx = chart.ctx
    const chartArea = chart.chartArea
    if (chartArea) {
      const chartWidth = chartArea.right - chartArea.left
      const chartHeight = chartArea.bottom - chartArea.top

      let backColor = options?.chartBackground || '#D0D0D0'
      if (typeof options?.chartBackground === 'function') {
        backColor = options?.chartBackground()
      }

      ctx.save()
      ctx.globalCompositeOperation = 'destination-over'
      ctx.fillStyle = backColor
      ctx.fillRect(chartArea.left, chartArea.top, chartWidth, chartHeight)
      ctx.restore()
    }
  },
}

export const CenterTextPlugIn = {
  id: `CenterTextPlugIn`,
  afterDraw: (chart, args, options) => {
    const {
      ctx,
      chartArea: { top, left, width, height },
    } = chart

    ctx.save()
    ctx.textBaseline = 'middle'
    ctx.textAlign = 'center'
    ctx.font = ctx.font = `${Math.max(Math.floor(height * 0.15), 1.5)}px Arial`
    ctx.fillStyle = options.color
    ctx.fillText(`${options.visible ? options.centerText : ''}`, left + width / 2, top + height / 2)
    ctx.restore()
  },
}

export const CustomMouseEventPlugIn = {
  id: 'CustomMouseEventPlugIn',
  beforeEvent: (chart, args, options) => {
    const event = args.event
    if (event.type === 'mousemove' || event.type === 'click') {
      const { x, y } = event
      const { top, left, right, bottom } = chart.chartArea
      if (x < left || x > right || y < top || y > bottom) {
      } else {
        event.native.preventDefault()
        event.native.stopImmediatePropagation()
      }
    }
  },
}

export const stackedTotalPlugin = {
  id: 'stackedTotal',
  afterDatasetsDraw(chart, args, pluginOptions) {
    if (!pluginOptions?.visible) return

    const { ctx, data, scales } = chart
    const yScale = scales.y
    const stackId = pluginOptions.stackId
    const unitStr = pluginOptions?.unit

    ctx.save()
    ctx.font = pluginOptions.font || '12px sans-serif'
    ctx.fillStyle = pluginOptions.color || '#fff'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'bottom'

    data.labels?.forEach((_, index) => {
      let total = 0

      data.datasets.forEach((ds) => {
        if (!stackId || ds.stack === stackId) {
          total += Number(ds.data[index] || 0)
        }
      })

      if (!total) return

      const meta = chart.getDatasetMeta(0)
      const bar = meta.data[index]
      if (!bar) return

      const x = bar.x
      const y = yScale.getPixelForValue(total) - 4

      ctx.fillText(`${total.toFixed(1)}${unitStr ?? ''}`, x, y)
    })

    ctx.restore()
  },
}

export const yAxisMaxZoomPlugin = {
  id: 'yAxisMaxZoomPlugin',
  afterInit: (chart) => {
    const handleWheel = (event) => {
      if (!event.shiftKey) return
      event.preventDefault()

      const zoomFactor = event.deltaY > 0 ? 1.1 : 0.9
      chart.options.scales.y.max = chart.scales.y.max * zoomFactor
      chart.options.scales.y.min = 0
      chart.update('none')
    }

    chart.canvas.addEventListener('wheel', handleWheel)
    chart._yAxisMaxZoomHandler = handleWheel
  },
  beforeDestroy: (chart) => {
    if (chart._yAxisMaxZoomHandler) {
      chart.canvas.removeEventListener('wheel', chart._yAxisMaxZoomHandler)
      delete chart._yAxisMaxZoomHandler
    }
  },
}

export const chartAreaReporterPlugin = {
  id: 'chartAreaReporter',
  afterLayout(chart) {
    const onUpdate = chart.options.plugins?.chartAreaReporter?.onUpdate
    if (!onUpdate) return
    const { left, top, width, height, right } = chart.chartArea
    onUpdate({ left, top, width, height, right })
  },
}

export const chartMeasurePlugin = {
  id: 'chartMeasure',

  afterDraw(chart, args, options) {
    const measure = chart.$measure

    if (!measure?.enabled) return

    const start = measure.start
    const end = measure.end ?? measure.cursor

    if (!start || !end) return

    const { ctx, scales, chartArea } = chart

    const x1 = scales.x.getPixelForValue(start.x)
    const y1 = scales.y.getPixelForValue(start.y)
    const x2 = scales.x.getPixelForValue(end.x)
    const y2 = scales.y.getPixelForValue(end.y)

    const dx = end.x - start.x
    const dy = end.y - start.y
    const distance = Math.hypot(dx, dy)

    ctx.save()

    // Line
    ctx.beginPath()
    ctx.moveTo(x1, y1)
    ctx.lineTo(x2, y2)
    ctx.strokeStyle = '#00d9ff'
    ctx.lineWidth = 2
    ctx.setLineDash([6, 4])
    ctx.stroke()

    ctx.setLineDash([])

    // End points
    ;[
      [x1, y1],
      [x2, y2],
    ].forEach(([x, y]) => {
      ctx.beginPath()
      ctx.arc(x, y, 5, 0, Math.PI * 2)
      ctx.fillStyle = '#00d9ff'
      ctx.fill()

      ctx.beginPath()
      ctx.arc(x, y, 2, 0, Math.PI * 2)
      ctx.fillStyle = '#fff'
      ctx.fill()
    })

    const label =
      options.formatLabel?.({
        start,
        end,
        dx,
        dy,
        distance,
      }) ?? distance.toFixed(2)

    drawLabel(ctx, chartArea, x1, y1, x2, y2, label)

    ctx.restore()
  },
}

const drawLabel = (ctx, chartArea, x1, y1, x2, y2, label) => {
  ctx.font = '12px Arial'

  const lines = Array.isArray(label) ? label : [label]

  const lineHeight = 16
  const padding = 6

  const textWidth = Math.max(...lines.map((line) => ctx.measureText(line).width))
  const boxHeight = lines.length * lineHeight + padding * 2

  const midX = (x1 + x2) / 2
  const midY = (y1 + y2) / 2

  const dx = x2 - x1
  const dy = y2 - y1
  const length = Math.hypot(dx, dy) || 1

  // Unit vector perpendicular to measurement line
  const normalX = -dy / length
  const normalY = dx / length

  const offset = 40

  let x = midX + normalX * offset
  let y = midY + normalY * offset - boxHeight / 2

  x = Math.max(chartArea.left + textWidth / 2 + padding, Math.min(chartArea.right - textWidth / 2 - padding, x))

  y = Math.max(chartArea.top, Math.min(chartArea.bottom - boxHeight, y))

  ctx.fillStyle = 'rgba(0, 0, 0, 0.75)'
  ctx.fillRect(x - textWidth / 2 - padding, y, textWidth + padding * 2, boxHeight)

  ctx.fillStyle = '#fff'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'

  lines.forEach((line, index) => {
    ctx.fillText(line, x, y + padding + lineHeight / 2 + index * lineHeight)
  })
}
